import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import * as React from "react";
import { useState } from "react";
import { submitContactEnquiry } from "@/utils/contact.functions";
import { Section } from "@/components/site/Section";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import contactPhoto from "@/assets/contact-call.jpg";
import { SITE_URL } from "@/lib/seo";

const TITLE = "Free Four-Week Pilot for Ten Managers | Leadership Edge Live";
const DESC =
  "Pick ten managers. We run them through one full live course for four weeks, at no charge. No card, no contract, no procurement.";

export const Route = createFileRoute("/pilot")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/pilot/` }],
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Pilot,
});

const fields = [
  { name: "firstName", label: "First name", type: "text", autoComplete: "given-name" },
  { name: "lastName", label: "Last name", type: "text", autoComplete: "family-name" },
  { name: "company", label: "Company", type: "text", autoComplete: "organization" },
  { name: "email", label: "Work email", type: "email", autoComplete: "email" },
  { name: "phone", label: "Phone", type: "tel", autoComplete: "tel" },
] as const;

const details = [
  {
    title: "What happens at the end",
    body: "You get to see who attended, when, and the work they completed. If you want to continue, it's $349 per person, per year. If you don't, we're done and all you've invested is a few hours of your managers' time.",
  },
  {
    title: "What we ask",
    body: "Pick people who manage someone, not volunteers looking for a break. Give us twenty honest minutes at the end about what worked and what didn't.",
  },
];

const labelClass = "text-xs font-semibold uppercase tracking-wider text-primary";

function Pilot() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const sendEnquiry = useServerFn(submitContactEnquiry);
  const openedAt = React.useRef(Date.now());

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const get = (k: string) => String(form.get(k) ?? "").trim();
    const phone = get("phone");
    if (phone.replace(/\D/g, "").length < 7) {
      setError("Please enter a valid phone number.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const notes = [
        "PILOT REQUEST: ten managers, four weeks",
        `Company: ${get("company")}`,
        `Phone: ${phone}`,
        get("notes") ? `Notes: ${get("notes")}` : "",
      ]
        .filter(Boolean)
        .join("\n");
      await sendEnquiry({
        data: {
          name: `${get("firstName")} ${get("lastName")}`.trim(),
          email: get("email"),
          notes,
          website: get("website"),
          elapsedMs: Date.now() - openedAt.current,
        },
      });
      setSubmitted(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message === "spam_suspected") setSubmitted(true);
      else setError(message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <section className="relative isolate overflow-hidden">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <img
            src={contactPhoto}
            alt=""
            width={1920}
            height={1088}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.3) 55%, rgba(255,255,255,0) 100%)",
            }}
          />
        </div>
        <div className="relative mx-auto flex min-h-[520px] max-w-6xl items-center px-6 py-16 md:min-h-[620px] md:py-20">
          <div className="max-w-[600px]">
            <h1
              className="font-sans text-4xl font-bold leading-[1.05] tracking-tight text-[#111111] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Pick ten managers. <span className="text-[#693D94]">Four weeks. No charge.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              No card, no contract, no procurement.
            </p>
          </div>
        </div>
      </section>

      <Section tone="default">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr]">
          <div>
            <h2
              className="text-3xl font-semibold leading-tight text-ink md:text-4xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              How the pilot works
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              You choose ten people who actually manage someone. We run them through one full
              course: a live 30-minute session each week with a real facilitator, an assignment they
              complete with their own team between sessions, and a Development Pro who follows up
              with each of them personally.
            </p>
            <div className="mt-8 space-y-6">
              {details.map((d) => (
                <div key={d.title}>
                  <h3
                    className="text-lg font-semibold text-ink"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {d.title}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-muted-foreground">{d.body}</p>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="h-fit rounded-2xl border border-border/60 bg-lavender p-7 shadow-brand md:p-9"
          >
            {submitted ? (
              <div className="py-6 text-center">
                <h3
                  className="text-2xl font-semibold leading-tight text-ink"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  You're in.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  We'll set up your pilot and follow up with you within one business day to pick a
                  course and get your ten managers enrolled.
                </p>
              </div>
            ) : (
              <>
                <h3
                  className="text-2xl font-semibold leading-tight text-ink"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Start your pilot
                </h3>
                <p className="mt-3 text-sm text-muted-foreground">
                  Tell us who you are. We'll set up the pilot and follow up with you.
                </p>

                <div className="mt-6 grid gap-5">
                  {fields.map((f) => (
                    <div key={f.name} className="grid gap-1.5">
                      <Label htmlFor={f.name} className={labelClass}>
                        {f.label}
                      </Label>
                      <Input
                        required
                        id={f.name}
                        name={f.name}
                        type={f.type}
                        autoComplete={f.autoComplete}
                        className="bg-background"
                      />
                    </div>
                  ))}
                  <div className="grid gap-1.5">
                    <Label htmlFor="notes" className={labelClass}>
                      Anything we should know? (optional)
                    </Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      rows={3}
                      className="rounded-[4px] border-border bg-background focus-visible:ring-2 focus-visible:ring-ring/40"
                    />
                  </div>
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      id="website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                </div>
                {error ? <p className="mt-5 text-sm text-destructive">{error}</p> : null}
                <button
                  type="submit"
                  disabled={sending}
                  className="mt-7 inline-flex w-full items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {sending ? "Sending…" : "Start my pilot"}
                </button>
              </>
            )}
          </form>
        </div>
      </Section>
    </>
  );
}
